import Hero from '@/components/Hero';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import SectionTitle from '@/components/SectionTitle';
import { Heart, Users, Shield, Home, FileText, Building2, Briefcase, Phone, MessageCircle } from 'lucide-react';
import aboutImage from '@/assets/about-image.jpg';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "יחס אישי וזמינות",
      description: "משרדנו שם דגש על יחס אישי, זמינות גבוהה ופתרונות מדויקים, מתוך אמונה שכל תיק הוא סיפור חיים בפני עצמו."
    },
    {
      icon: Shield,
      title: "מקצועיות ואנושיות",
      description: "הגישה שלנו משלבת מקצועיות ונחישות עם רגישות ואנושיות, מתוך מטרה אחת – להוביל את הלקוח בביטחון אל התוצאה הנכונה עבורו, אנחנו 'תופרים' כל חליפה על פי מידות הלקוח."
    },
    {
      icon: Users,
      title: "הבנה אמיתית",
      description: "אנחנו מאמינים שעורך דין טוב וייצוג טוב הוא לא רק מי שמכיר את החוק - אלא מי שמבין אנשים."
    }
  ];

  const expertise = [
    {
      icon: Home,
      title: "מקרקעין ומיסוי מקרקעין",
      description: "ייצוג מקיף בעסקאות מכר, ירושה ומיסוי"
    },
    {
      icon: FileText,
      title: "חדלות פירעון והסדרי נושים",
      description: "ליווי והסדרת חובות בתהליכים מורכבים"
    },
    {
      icon: Building2,
      title: "הוצאה לפועל וגבייה",
      description: "גבייה אפקטיבית ויעילה של חובות"
    },
    {
      icon: Briefcase,
      title: "ירושה ופירוק שיתוף",
      description: "טיפול מקצועי בירושות ופירוק שיתופים"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <Hero 
        title="אודות המשרד" 
        subtitle="ייצוג משפטי מקצועי ואנושי" 
        image={aboutImage}
        height="medium" 
      />

      {/* מי אנחנו */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <SectionTitle 
              title="מי אנחנו" 
              align="center"
            />
            <p className="text-lg md:text-xl text-foreground/80 leading-relaxed">
              משרדנו עוסק בייצוג משפטי בתחומי המקרקעין, מיסוי מקרקעין, חדלות פירעון והוצאה לפועל, 
              ומלווה לקוחות פרטיים ועסקיים בתהליכים מורכבים של מכירה, ירושה, פירוק שיתוף, 
              גבייה והסתבכויות כלכליות.
            </p>
          </div>
        </div>
      </section>

      {/* הערכים שלנו */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="הערכים שלנו" 
            align="center"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-card hover:shadow-lg transition-all duration-300 hover:scale-105 border-border"
                >
                  <CardContent className="p-6 md:p-8 text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-3 text-card-foreground">{value.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* תחומי התמחות */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="תחומי ההתמחות שלנו" 
            align="center"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {expertise.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-card hover:shadow-lg transition-all duration-300 hover:scale-105 border-border"
                >
                  <CardContent className="p-6 text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-medium mb-2 text-card-foreground">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-4">מוכנים להתחיל?</h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            צרו קשר עוד היום לייעוץ ראשוני
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild 
              size="lg" 
              variant="secondary"
              className="min-w-[200px]"
            >
              <Link to="/contact">
                <Phone className="ml-2 h-5 w-5" />
                צור קשר
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="min-w-[200px] bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20"
              onClick={() => window.open('https://wa.me/972502240855', '_blank')}
            >
              <MessageCircle className="ml-2 h-5 w-5" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
